import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { PlusSquare, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import {
  checkIsExpire,
  GetFilterUrl,
  productOptions,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import PageHeading from '@/components/common/PageHeading';
import CommonFilter from '@/components/common/CommonFilter';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import Pagination from '@/components/common/Pagination';
import ImagePreview from '@/components/common/media/ImagePreview';
import StatusDropdown from '@/components/common/StatusComponent';
import { RoutesLink } from '@/utility/RouteLinks';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';

interface ProductStates {
  isLoading: boolean;
  productStatus: string;
  products: IproductResponse[];
  pageInfo: PageInfo;
  selectedProducts: Set<string | number>;
  showDeleteModel: boolean;
  productToDelete: string | null;
}

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete, canCreate } = usePermissions();
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [states, setStates] = useState<ProductStates>({
    isLoading: false,
    productStatus: 'active',
    products: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    selectedProducts: new Set(),
    showDeleteModel: false,
    productToDelete: null,
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const onStatusUpdate = async (id: string, status: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${id}`;
      await callApi(PATH, { method: 'PATCH', data: { status } });
      fetchProducts();
    } catch (error) {
      console.log('error', error);
      handleChangeState({ isLoading: false });
    }
  };

  // PRODUCT LIST FETCHING
  const fetchProducts = async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.productStatus,
        search: debounceValue,
        minDiscount: 1,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? `${query}` : ''}`;
      const res = await callApi<IproductResponseList>(PATH, { method: 'GET' });
      handleChangeState({
        products: res.data,
        pageInfo: {
          limit: res.pageInfo.limit,
          page: res.pageInfo.page,
          total: res.pageInfo.total,
          totalPages: res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
      console.log('error is', error);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
      handleChangeState({
        pageInfo: {
          ...states.pageInfo,
          page: 1,
        },
      });
      fetchProducts();
      showToastNotification('success', 'Product deleted successfully');
    } catch (error: unknown) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error', error);
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.productStatus,
    debounceValue,
  ]);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.products);

  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => {
        const url = item?.directImageUrl
          ? item?.directImageUrl
          : item?.media?.[0]?.url;
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${url}`}
            alt={''}
            text={item.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={productOptions}
          onChange={(status) => {
            onStatusUpdate(item.id, status);
          }}
        />
      ),
    },
    {
      key: 'stock',
      header: 'Unit Stock',
      render: (item) => item.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'CTN Stock',
      header: 'CTN Stock',
      render: (item) => item.productInfo[0]?.primaryQuantity || 0,
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) => (
        <span>
          {checkIsExpire(item?.productInfo[0]?.discountEndDate) ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'averageRating',
      header: 'Rating',
      render: (item) => (
        <div className="p-3 flex items-center gap-1">
          {toFixedThree(item.averageRating || 0, 1)}{' '}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  const discountActionConfig: Record<string, ActionConfig<IproductResponse>> = {
    eidt: {
      text: 'Edit',
      onClick: (product: IproductResponse) =>
        navigate(`${RoutesLink.addProducts}/${product.id}`),
      permission: () => canEdit('product'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(discountActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canEdit, canDelete],
  );

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <Layout>
      <PageHeading
        text="Discounted Products"
        button={
          canCreate('discount')
            ? {
                icon: <PlusSquare />,
                text: 'Add Discount',
                onClick: () => {
                  navigate(RoutesLink.addDiscount);
                },
              }
            : undefined
        }
      />

      <Card>
        <CommonFilter
          filters={productOptions}
          inputPlaceHolder="Search for products"
          onChange={(filter) => {
            handleChangeState({
              productStatus: filter,
              pageInfo: { ...states.pageInfo, page: 1 },
            });
          }}
          onSearch={(text) => setSearchValue(text)}
        />
        <CommonTable
          columns={columns}
          data={states.products}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No products found"
          loading={states.isLoading}
          actions={actions}
        />
        <Pagination
          isLoading={states.isLoading}
          paginationData={states.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>
      {states.showDeleteModel && (
        <ConfirmationModel
          show={true}
          onClose={() => {
            handleChangeState({ showDeleteModel: false });
          }}
          type="product"
          onConfirm={() =>
            handleDeleteProduct(states.productToDelete as string)
          }
        />
      )}
    </Layout>
  );
};

export default Products;
